import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import TaskSkeleton from './TaskSkeleton';

export default function StreamVidCategoryList() {
    const { http, user } = AuthUser();
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = () => {
        setIsLoading(true);
        http.get('/stream_vidcategories').then((res) => {
            setIsLoading(false);
            if (res.data.success && Array.isArray(res.data.data)) {
                setCategories(res.data.data);
            } else {
                console.error("API response error:", res.data);
                toast.error("Failed to fetch video categories");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching video categories:", error);
            toast.error("Error fetching video categories");
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            let response;
            if (editingCategory) {
                // Use POST with _method for updates (Laravel method spoofing)
                response = await http.post(`/stream_vidcategories/${editingCategory.id}`, {
                    _method: 'PUT',
                    name: formData.name
                });
                toast.success("Video category updated successfully!");
            } else {
                response = await http.post('/stream_vidcategories', {
                    name: formData.name
                });
                toast.success("Video category created successfully!");
            }

            if (response.data.success) {
                setShowModal(false);
                setFormData({
                    name: ''
                });
                setEditingCategory(null);
                fetchCategories();
            }
        } catch (error) {
            console.error("Error saving video category:", error);
            const errorMessage = error.response?.data?.message || "Error saving video category";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this video category?")) {
            try {
                const response = await http.delete(`/stream_vidcategories/${id}`);
                if (response.data.success) {
                    toast.success("Video category deleted successfully!");
                    fetchCategories();
                }
            } catch (error) {
                console.error("Error deleting video category:", error);
                toast.error("Error deleting video category");
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: ''
        });
        setEditingCategory(null);
        setShowModal(false);
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title">Video Categories</h3>
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowModal(true)}
                            >
                                + Add Video Category
                            </button>
                        </div>
                        <div className="card-body">
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {categories.length > 0 ? (
                                                categories.map((category) => (
                                                    <tr key={category.id}>
                                                        <td>{category.id}</td>
                                                        <td>{category.name}</td>
                                                        <td>{moment(category.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                        <td>
                                                            <button 
                                                                className="btn btn-sm btn-warning me-2"
                                                                onClick={() => handleEdit(category)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDelete(category.id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No video categories found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingCategory ? 'Edit Video Category' : 'Add New Video Category'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={resetForm}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

